import React, { useEffect, useState } from "react";
import { Switch, Redirect, BrowserRouter, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { PrivateRoute, PublicRoute } from "./components";
import {
  Main as MainLayout,
  Minimal as MinimalLayout,
} from "./components/layouts";
import {
  Home as HomeView,
  UserList as UserListView,
  SchoolRouter as SchoolListView,
  SchoolStudentsForm as StudentsView,
  StudentList as StudentListView,
  SignUp as SignUpView,
  ValidateRegisterIs,
  ValidateRegisterPs,
  ValidateRegisterCs,
  ConfirmationRegisterIs as confirmationRegisterIsView,
  SignIn as SignInView,
  SignInBySchoolSwitch as SignInBySchoolView,
  NotFound as NotFoundView,
  Dashboard as DashboardView,
  Contact as ContactView,
  Documents as DocumentsView,
  DeclareClaim as DeclareClaimView,
  HealthMobilityCertificate as HealthMobilityCertificateView,
  ClaimsMonitoring as ClaimsMonitoringView,
  PaymentRedirect as PaymentRedirectionView,
  PaymentRedirectMultimedia as PaymentRedirectionMultimediaView,
  MultimediaInsurance as MultimediaInsuranceView,
  RedirectHealthMutual as RedirectHealthMutualView,
  SignView as SignSellAndSignView

} from "./views";
import HabitationV2Page from './features/habitation/pages/HabitationPage';
import RcPage from './features/rc/pages/RcPage';
import SanteMobilitePage from './features/sante-mobilite/pages/SanteMobilitePage';
import SanteMobiliteSuccessPage from './features/sante-mobilite/pages/SanteMobiliteSuccessPage';
import SanteMobiliteSuccessPaymentPage from './features/sante-mobilite/pages/SanteMobiliteSuccessPaymentPage';
import RcSuccessPaymentPage from './features/rc/pages/RcSuccessPaymentPage';
import RcSuccessSignaturePage from './features/rc/pages/SuccessPage';
import ProfilePage from './features/profile/pages/ProfilePage';

import HealthMobilityEstimationPage from "./views/InsuranceEstimation/HealthMobilityEstimationPage";
import HabitationEstimationPage from "./views/InsuranceEstimation/HabitationEstimationPage";

import ResetPassword from "./views/ResetPassword/ResetPassword";

import LocalStorageService from "./services/LocalStorageService";
import { isAuthenticated } from "./services/utils";
import SuccessPaymentPage from "./features/habitation/pages/SuccessPaymentPage";
import SuccessSignaturePage from "./features/habitation/pages/SuccessSignaturePage";

// Component to redirect from root based on user role
const RootRedirect = () => {
  // Get user from both Redux (fresh after login) and localStorage (persists after refresh)
  const authState = useSelector((state) => state.auth);
  const localUser = LocalStorageService.getUser();

  // Merge user data: prioritize Redux user if available (fresh after login), fallback to localStorage
  const currentUser = {
    ...localUser,
    // Use Redux user data if available (includes fresh roles after login)
    ...(authState?.data?.user || {})
  };

  // Determine if user is admin
  const isAdmin = currentUser?.roles && currentUser.roles.length > 0 && currentUser.roles[0] === 'ROLE_ADMIN';

  // Redirect admins to /schools, students to /home
  return <Redirect to={isAdmin ? "/schools" : "/home"} />;
};

export const AppRouter = () => {
  const currentUser = LocalStorageService.getUser();
  const [isConnected, setIsconnected] = useState(false);

  useEffect(() => {
    if (!currentUser.email) {
      const data = isAuthenticated();
      if (!data) {
        setIsconnected(false);
      }
    }else{
      setIsconnected(true);
    }
  }, []);
  return (
    <BrowserRouter>
      <Switch>
        <Route exact path="/" component={RootRedirect} />
        <PrivateRoute
          component={HomeView}
          exact
          layout={MainLayout}
          path="/home"
        />
        <PrivateRoute
          component={DashboardView}
          exact
          layout={MainLayout}
          path="/dashboard"
        />
        <PrivateRoute
          component={UserListView}
          exact
          layout={MainLayout}
          path="/users"
        />
        <PrivateRoute
          component={SchoolListView}
          exact
          layout={MainLayout}
          path="/schools"
        />
        <PrivateRoute
          component={StudentsView}
          exact
          layout={MainLayout}
          path="/student/create"
        />
        <PrivateRoute
          component={StudentListView}
          exact
          layout={MainLayout}
          path="/student/list"
        />
        <PrivateRoute
          component={ContactView}
          exact
          layout={MainLayout}
          image={"/images/contact.jpg"}
          imageStyle={"diag"}
          direction={'left'}
          path="/contact"
        />
        <PrivateRoute
          component={DeclareClaimView}
          exact
          layout={MainLayout}
          path="/claims/declare"
        />
        <PrivateRoute
          component={DocumentsView}
          exact
          layout={MainLayout}
          path="/documents"
        />
        <PrivateRoute
          component={ProfilePage}
          exact
          layout={MainLayout}
          path="/profile"
        />
        <PublicRoute
          component={SanteMobilitePage}
          exact
          layout={MinimalLayout}
          path="/insurance/health-mobility"
        />
        <PublicRoute
          component={SanteMobiliteSuccessPaymentPage}
          exact
          layout={MinimalLayout}
          path="/insurance/health-mobility/success-payment"
        />
        <PublicRoute
          component={SanteMobiliteSuccessPage}
          exact
          layout={MinimalLayout}
          path="/insurance/health-mobility/success"
        />
        <PrivateRoute
          component={ MultimediaInsuranceView }
          exact
          layout={ MainLayout}
          direction={'right'}
          path="/insurance/multimedia" />
        <PublicRoute
          component={ HealthMobilityCertificateView }
          exact
          layout={MainLayout}
          path="/insurance/health-mobility/certificate"
        />
        <PublicRoute
          component={ RedirectHealthMutualView }
          exact
          layout={MainLayout}
          path="/insurance/health-mutual"
        />       
        <PublicRoute
          component={SignSellAndSignView}
          exact
          layout={MinimalLayout}
          path="/insurance/sign"
        />
        <PublicRoute
          component={ HealthMobilityEstimationPage }
          exact
          layout={MinimalLayout}
          path="/estimation/health-mobility"
        />
        <PublicRoute
          component={ HabitationEstimationPage }
          exact
          layout={MinimalLayout}
          path="/estimation/habitation"
        />
        <PublicRoute
          component={ ResetPassword }
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/forgot-password"
        />
        <PublicRoute
          component={ PaymentRedirectionView }
          exact
          layout={MainLayout}
          path="/checkout"
        />
        <PublicRoute
          component={ PaymentRedirectionMultimediaView }
          exact
          layout={MainLayout}
          path="/multimedia_checkout"
        />      
        <PublicRoute
          component={SignInView}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/login"
        />
        <PublicRoute
          component={SignInBySchoolView}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/login/:schoolName"
        />
        <PublicRoute
          component={SignUpView}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/register"
        />
        <PublicRoute
          component={ValidateRegisterIs}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/confirmation_is"
        />
        <PublicRoute
          component={ValidateRegisterPs}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/confirmation_ps"
        />
        <PublicRoute
          component={ValidateRegisterCs}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/confirmation_cs"
        />
        <PublicRoute
          component={confirmationRegisterIsView}
          exact
          layout={MinimalLayout}
          image={"/images/home_picture1.jpg"}
          path="/register_is"
        />
        <PrivateRoute
          component={ClaimsMonitoringView}
          exact
          layout={MainLayout}
          path="/claims/my-claims"
        />
        <PublicRoute
          component={NotFoundView}
          exact
          layout={MinimalLayout}
          path="/not-found"
        />
        <PublicRoute
          component={HabitationV2Page}
          exact
          layout={MinimalLayout}
          path="/insurance/housing"
        />
        <PublicRoute
          component={SuccessPaymentPage}
          exact
          layout={MinimalLayout}
          path="/insurance/housing/success-payment"
        />
        <PublicRoute
          component={SuccessSignaturePage}
          exact
          layout={MinimalLayout}
          path="/insurance/housing/success-signature"
        />
        <PublicRoute
          component={RcPage}
          exact
          layout={MinimalLayout}
          path="/insurance/rc"
        />
        <PublicRoute
          component={RcSuccessPaymentPage}
          exact
          layout={MinimalLayout}
          path="/insurance/rc/success-payment"
        />
        <PublicRoute
          component={RcSuccessSignaturePage}
          exact
          layout={MinimalLayout}
          path="/insurance/rc/success-signature"
        />
        <Redirect to="/not-found" />
      </Switch>
    </BrowserRouter>
  );
};
